// pages/index/components/Sidebar.tsx
import { Fragment, useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ExpandLess, ExpandMore, Search, Close } from "@mui/icons-material";
import { useAccountContext } from "@root/stores/accountStore";
import { navigate } from "vike/client/router";
import { usePageContext } from "@root/renderer/usePageContext";
import { LoadingOverlay } from "../common/LoadingOverlay";
import { UserRoutes } from "./UserRoutes";
import { OrganizationRoutes } from "./OrganizationRoutes";
import { AdminRoutes } from "./AdminRoutes";
import { MenuRoute } from "./MenuRoute";
import { sidebarStore } from "@root/stores/sidebarStore";

type SidebarProps = {
  isOpen: boolean;
};

export const Sidebar = observer(({ isOpen }: SidebarProps) => {
  const accountStore = useAccountContext();
  const pageContext = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isNavigating, setIsNavigating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Use urlPathname from pageContext which automatically updates on route changes
  const currentPath = pageContext.urlPathname;

  const sessionId = useMemo(
    () => accountStore.session?.id,
    [accountStore.session]
  );

  // Memoize routes to prevent unnecessary recalculations
  const routes = useMemo(() => {
    const baseRoutes =
      accountStore.session?.role === "Admin"
        ? AdminRoutes()
        : accountStore.isUserProfile
          ? [...UserRoutes] // Create a new array to avoid mutating the original
          : OrganizationRoutes(sessionId);

    // Return a new array to ensure referential stability
    return baseRoutes.map((route) => ({ ...route }));
  }, [accountStore.session?.role, accountStore.isUserProfile, sessionId]);

  // Track expanded state separately to avoid mutating the routes
  const [expandedRoutes, setExpandedRoutes] = useState<Record<string, boolean>>(
    {}
  );

  const expandMenuItem = useCallback((route: MenuRoute) => {
    setExpandedRoutes((prev) => ({
      ...prev,
      [route.text]: !prev[route.text],
    }));
  }, []);

  // Helper to get expanded state for a route
  const isRouteExpanded = useCallback(
    (route: MenuRoute) => {
      return expandedRoutes[route.text] ?? route.expanded ?? false;
    },
    [expandedRoutes]
  );

  // Helper to check if a route is active - only exact matches
  const isRouteActive = useCallback(
    (route: MenuRoute) => {
      if (!route.href || !currentPath) return false;
      // Only exact match, no prefix matching
      return currentPath === route.href;
    },
    [currentPath]
  );

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim()) {
      setIsNavigating(true);

      // Navigate to search page with query parameter
      await navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery(""); // Clear search after navigation
      setIsNavigating(false);
    }
  }, [searchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const getMenuListItem = (route: MenuRoute) => {
    const isActive = isRouteActive(route);

    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={async () => {
            if (route.onClick) {
              setIsNavigating(true);
              if (isMobile) sidebarStore.close();
              route.onClick();
              setIsNavigating(false);
            } else {
              setIsNavigating(true);
              if (isMobile) sidebarStore.close();
              await navigate(route.href!);
              setIsNavigating(false);
            }
          }}
          sx={{
            borderRadius: "8px",
            margin: "2px 8px",
            backgroundColor: isActive
              ? "rgba(25, 118, 210, 0.08)"
              : "transparent",
            "&:hover": {
              backgroundColor: isActive
                ? "rgba(25, 118, 210, 0.12)"
                : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          {route.icon && (
            <ListItemIcon
              sx={{
                color: isActive
                  ? theme.palette.primary.main
                  : "rgba(0, 0, 0, 0.54)",
              }}
            >
              {route.icon()}
            </ListItemIcon>
          )}
          <ListItemText
            primary={route.text}
            sx={{
              "& .MuiListItemText-primary": {
                color: isActive ? theme.palette.primary.main : "inherit",
                fontWeight: isActive ? 600 : 400,
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // On mobile, show as overlay. On desktop, show as fixed sidebar
  const sidebarStyles = {
    position: "fixed" as const,
    top: isMobile ? 0 : "64px", // Full screen on mobile, below header on desktop
    left: 0,
    height: isMobile ? "100vh" : "calc(100vh - 64px)", // Full height on mobile, adjusted height on desktop
    width: "280px",
    backgroundColor: "#f4f8fb",
    borderRight: isOpen ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
    transform: isOpen ? "translateX(0)" : "translateX(-100%)", // Slide in/out
    transition: "transform 0.3s ease-in-out",
    overflow: "auto",
    zIndex: isMobile ? 1400 : 1000, // Above backdrop (1300) but below AppBar (1500) on mobile, below AppBar on desktop
    boxShadow: isOpen ? "2px 0 8px rgba(0, 0, 0, 0.15)" : "none",
    [theme.breakpoints.down("md")]: {
      width: "75vw",
      maxWidth: "300px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "80vw",
      maxWidth: "280px",
    },
  };

  // Mobile overlay backdrop
  const backdropStyles = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1300, // Above content but below sidebar (1400) and AppBar (1500)
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? ("visible" as const) : ("hidden" as const),
    transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
  };

  return (
    <Fragment>
      {/* Mobile backdrop */}
      {isMobile && (
        <Box sx={backdropStyles} onClick={() => sidebarStore.close()} />
      )}

      {/* Sidebar */}
      <Box sx={sidebarStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            maxWidth: "280px",
            padding: isMobile
              ? {
                  xs: "48px 0 1rem 0", // Small mobile AppBar height
                  md: "56px 0 1rem 0", // Medium mobile AppBar height
                }
              : "1rem 0", // Account for AppBar height on mobile
            [theme.breakpoints.down("md")]: {
              maxWidth: "320px",
            },
            [theme.breakpoints.down("sm")]: {
              maxWidth: "280px",
            },
          }}
        >
          {/* Header */}
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 1rem",
                marginBottom: "1rem",
                marginTop: isMobile ? "0.5rem" : "0",
              }}
            ></Box>

            {/* Search Events */}
            <Box
              sx={{
                padding: "0 1rem",
                marginBottom: { xs: "-3rem", sm: "-3rem" },
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search Events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery.trim() && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{
                          padding: "4px",
                          color: "#e60000",
                          "&:hover": {
                            backgroundColor: "rgba(230, 0, 0, 0.08)",
                          },
                        }}
                      >
                        <Close sx={{ fontSize: "1.2rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "white",
                    },
                  },
                }}
              />
            </Box>

            {/* Discover Events */}
            <List sx={{ paddingBottom: 0 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={async () => {
                    setIsNavigating(true);
                    if (isMobile) sidebarStore.close();
                    await navigate("/");
                    setIsNavigating(false);
                  }}
                  sx={{
                    borderRadius: "20px",
                    margin: "2px 8px",
                    backgroundColor:
                      currentPath === "/"
                        ? "rgba(25, 118, 210, 0.08)"
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        currentPath === "/"
                          ? "rgba(25, 118, 210, 0.12)"
                          : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color:
                        currentPath === "/"
                          ? theme.palette.primary.main
                          : "rgba(0, 0, 0, 0.54)",
                    }}
                  >
                    <Search />
                  </ListItemIcon>
                  <ListItemText
                    primary="Discover Events"
                    sx={{
                      "& .MuiListItemText-primary": {
                        color:
                          currentPath === "/"
                            ? theme.palette.primary.main
                            : "inherit",
                        fontWeight: currentPath === "/" ? 600 : 400,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ margin: "0.5rem 0" }} />

          {/* Navigation Routes */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <List>
              {routes.map((route, i) => (
                <Fragment key={`route${i}_${route.href}`}>
                  {route.expandable ? (
                    <>
                      {route.expanded && (
                        <Divider
                          sx={{
                            height: "2px",
                            background: "#BBB",
                            margin: "4px 8px",
                          }}
                        />
                      )}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => expandMenuItem(route)}
                          sx={{
                            borderRadius: "8px",
                            margin: "2px 8px",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {route.icon && (
                            <ListItemIcon
                              sx={(theme) => ({
                                color: isRouteExpanded(route)
                                  ? theme.palette.primary.main
                                  : "rgba(0, 0, 0, 0.54)",
                              })}
                            >
                              {route.icon()}
                            </ListItemIcon>
                          )}
                          <ListItemText primary={route.text} />
                          {isRouteExpanded(route) ? (
                            <ExpandLess
                              sx={(theme) => ({
                                color: theme.palette.primary.main,
                              })}
                            />
                          ) : (
                            <ExpandMore />
                          )}
                        </ListItemButton>
                      </ListItem>
                      <Collapse
                        in={isRouteExpanded(route)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List
                          component="div"
                          disablePadding
                          sx={{ paddingLeft: "1rem" }}
                        >
                          {route.subRoutes?.map((subRoute: MenuRoute, j: number) => (
                            <Fragment key={`route${i}_${j}`}>
                              {getMenuListItem(subRoute)}
                            </Fragment>
                          ))}
                        </List>
                      </Collapse>
                      {route.expanded && (
                        <Divider
                          sx={{
                            height: "2px",
                            background: "#BBB",
                            margin: "4px 8px",
                          }}
                        />
                      )}
                    </>
                  ) : (
                    getMenuListItem(route)
                  )}
                </Fragment>
              ))}
            </List>
          </Box>
        </Box>
      </Box>

      <LoadingOverlay show={isNavigating} />
    </Fragment>
  );
});
